import React, { useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ISO numeric → ISO2 mapping for the countries we care about
const ISO_NUMERIC_TO_2: Record<string, string> = {
  '840': 'US', '826': 'GB', '360': 'ID', '404': 'KE', '528': 'NL',
  '524': 'NP', '724': 'ES', '158': 'TW', '800': 'UG', '124': 'CA',
  '756': 'CH', '208': 'DK', '710': 'ZA', '414': 'KW', '858': 'UY',
  '136': 'KY', '566': 'NG', '276': 'DE', '250': 'FR',
  '036': 'AU', '392': 'JP', '076': 'BR', '156': 'CN', '356': 'IN',
  '380': 'IT', '410': 'KR', '702': 'SG', '764': 'TH', '784': 'AE',
};

interface CountryStat {
  country: string;
  countryIso2: string;
  total: number;
  standalone: number;
  bundled: number;
}

interface Props {
  byCountry: CountryStat[];
  onCountryClick: (country: string) => void;
}

function getColor(count: number, max: number): string {
  if (count === 0) { return '#e2e8f0'; }
  const intensity = Math.max(0.2, count / max);
  // Blue scale from light to dark
  const r = Math.round(239 - intensity * (239 - 37));
  const g = Math.round(246 - intensity * (246 - 99));
  const b = Math.round(255 - intensity * (255 - 235));
  return `rgb(${r},${g},${b})`;
}

export function AMBRWorldMap({ byCountry, onCountryClick }: Props) {
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [position, setPosition] = useState<{ coordinates: [number, number]; zoom: number }>({
    coordinates: [0, 20],
    zoom: 1,
  });

  const countryMap = new Map<string, CountryStat>(
    byCountry.map(c => [c.countryIso2.toUpperCase(), c])
  );
  const max = Math.max(...byCountry.map(c => c.total), 1);

  return (
    <div style={{ position: 'relative', userSelect: 'none' }}>
      {/* Zoom controls */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button
          onClick={() => setPosition(p => ({ ...p, zoom: Math.min(p.zoom * 1.5, 8) }))}
          style={{ width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
        >+</button>
        <button
          onClick={() => setPosition(p => ({ ...p, zoom: Math.max(p.zoom / 1.5, 1) }))}
          style={{ width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
        >−</button>
        <button
          onClick={() => setPosition({ coordinates: [0, 20], zoom: 1 })}
          style={{ width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: '#64748b' }}
          title="Reset view"
        >⊙</button>
      </div>

      <ComposableMap
        projectionConfig={{ scale: 147 }}
        style={{ width: '100%', height: 'auto' }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={({ coordinates, zoom }: { coordinates: [number, number]; zoom: number }) => setPosition({ coordinates, zoom })}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }: { geographies: any[] }) =>
              geographies.map((geo: any) => {
                const numericId = geo.id?.toString().padStart(3, '0') ?? '';
                const iso2 = ISO_NUMERIC_TO_2[numericId] ?? '';
                const stat = countryMap.get(iso2);
                const count = stat?.total ?? 0;
                const fill = getColor(count, max);
                const hasData = count > 0;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#fff"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none', cursor: hasData ? 'pointer' : 'default' },
                      hover: { outline: 'none', fill: hasData ? '#2563eb' : '#d1d5db', cursor: hasData ? 'pointer' : 'default' },
                      pressed: { outline: 'none' },
                    }}
                    onMouseEnter={(e: React.MouseEvent) => {
                      if (stat) {
                        setTooltip({
                          text: `${stat.country}: ${stat.total} order${stat.total !== 1 ? 's' : ''} (${stat.standalone} standalone, ${stat.bundled} bundled)`,
                          x: e.clientX,
                          y: e.clientY,
                        });
                      }
                    }}
                    onMouseLeave={() => setTooltip(null)}
                    onClick={() => { if (stat) { onCountryClick(stat.country); } }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: tooltip.x + 12,
          top: tooltip.y - 8,
          background: '#0f172a',
          color: '#f8fafc',
          padding: '0.4rem 0.75rem',
          borderRadius: 6,
          fontSize: '0.78rem',
          pointerEvents: 'none',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          maxWidth: 260,
        }}>
          {tooltip.text}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.72rem', color: '#94a3b8' }}>
        <span>0</span>
        <div style={{ display: 'flex', gap: 2 }}>
          {[0.15, 0.3, 0.5, 0.7, 1].map((t, i) => (
            <div key={i} style={{ width: 20, height: 10, borderRadius: 2, background: getColor(Math.round(t * max), max) }} />
          ))}
        </div>
        <span>{max}+ orders</span>
        <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>Drag to pan · scroll/pinch to zoom · click country to drill in</span>
      </div>
    </div>
  );
}
